import { ReactNode, useEffect, useMemo, useState } from "react";
import { getSessionStorage, setSessionStorage } from "helpers/session-storage-helper/session-storage-helper";
import { getCookieConsent } from "helpers/cookie-helper/cookie-helper";
import { AppTrackerContext } from "./useAppTracker";

type AppTrackerProviderProps = {
	children: ReactNode;
};

type TelemetryData = {
	appStartTime: number;
};

export const AppTrackerProvider = ({ children }: AppTrackerProviderProps) => {
	const [wentToCheckoutDate, setWentToCheckoutDate] = useState<number | null>(null);

	useEffect(() => {
		const telemetryDataFromSessionStorage = getSessionStorage<TelemetryData>("telemetry");
		if (telemetryDataFromSessionStorage) {
			// If telemetry data already exists, do not overwrite it
			return;
		}

		const telemetryData: TelemetryData = {
			appStartTime: Date.now(),
		};

		setSessionStorage("telemetry", telemetryData);
	}, []);

	const value = useMemo(() => {
		const getMillisecondsSinceStart = (): number | null => {
			const cookieConsent = getCookieConsent();
			if (!cookieConsent?.analytics) {
				return null;
			}

			const telemetryData = getSessionStorage<TelemetryData>("telemetry");
			if (!telemetryData) {
				return null;
			}

			return Date.now() - telemetryData.appStartTime;
		};

		const checkoutLoaded = () => {
			setWentToCheckoutDate(Date.now());
		};

		const getMillisecondsSinceCheckoutLoaded = (): number | null => {
			const cookieConsent = getCookieConsent();
			if (!cookieConsent?.analytics) {
				return null;
			}
			if (wentToCheckoutDate === null) {
				return null;
			}

			return Date.now() - wentToCheckoutDate;
		};

		return {
			getMillisecondsSinceStart,
			checkoutLoaded,
			getMillisecondsSinceCheckoutLoaded,
		};
	}, [wentToCheckoutDate]);

	return <AppTrackerContext.Provider value={value}>{children}</AppTrackerContext.Provider>;
};
